import React, { useEffect, useState, useContext } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import api from "../services/api";
import { trackPurchase } from "../services/analytics";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap');

.ps-page {
  background: #f9f7f4;
  min-height: 100vh;
  font-family: 'DM Sans', sans-serif;
  padding: 60px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ps-card {
  background: #fff;
  border-radius: 24px;
  border: 1px solid #e8e4df;
  padding: 48px 40px;
  width: 100%;
  max-width: 540px;
  text-align: center;
  box-shadow: 0 8px 40px rgba(0,0,0,0.06);
}

/* Success icon — animated check */
.ps-success-circle {
  width: 90px; height: 90px;
  background: #e8f3ed;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 24px;
  animation: psPop 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55);
}
.ps-success-icon {
  font-size: 2.6rem; color: #1a5c38;
  animation: psCheck 0.45s 0.2s both;
}
@keyframes psPop { 0% { transform: scale(0); } 100% { transform: scale(1); } }
@keyframes psCheck {
  0%   { transform: scale(0); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

/* Failure icon */
.ps-fail-circle {
  width: 90px; height: 90px;
  background: #fef2f2;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 24px;
}
.ps-fail-icon { font-size: 2.6rem; color: #d94f3d; }

.ps-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.9rem; font-weight: 700;
  color: #1a1a1a; margin-bottom: 10px;
}
.ps-subtitle {
  font-size: 0.95rem; color: #6a6a6a;
  line-height: 1.6; margin-bottom: 32px;
}

/* Order details box */
.ps-details {
  background: #f9f7f4;
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 24px;
  text-align: left;
  border: 1px solid #e8e4df;
}
.ps-detail-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 9px 0;
  font-size: 0.88rem;
  border-bottom: 1px solid #e8e4df;
}
.ps-detail-row:last-child { border-bottom: none; }
.ps-detail-label { color: #8a8a8a; }
.ps-detail-value { color: #1a1a1a; font-weight: 600; word-break: break-all; text-align: right; }
.ps-detail-value.amount {
  color: #1a5c38; font-size: 1.05rem;
  font-family: 'Playfair Display', serif;
}

/* Buttons */
.ps-buttons {
  display: flex; gap: 12px;
  flex-wrap: wrap; justify-content: center;
}
.ps-btn-primary {
  background: #1a5c38; color: #fff;
  border: none; border-radius: 100px;
  padding: 13px 28px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.92rem; font-weight: 600;
  cursor: pointer; transition: background 0.15s;
  text-decoration: none;
  display: inline-block;
}
.ps-btn-primary:hover { background: #134429; color: #fff; }
.ps-btn-outline {
  background: transparent; color: #1a5c38;
  border: 2px solid #1a5c38; border-radius: 100px;
  padding: 11px 26px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.92rem; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
  text-decoration: none;
  display: inline-block;
}
.ps-btn-outline:hover { background: #1a5c38; color: #fff; }

/* Loading state */
.ps-loading {
  text-align: center; padding: 40px 0;
}
.ps-spinner {
  width: 40px; height: 40px;
  border: 3px solid #e8e4df; border-top-color: #1a5c38;
  border-radius: 50%; animation: psSpin 0.8s linear infinite;
  margin: 0 auto 16px;
}
@keyframes psSpin { to { transform: rotate(360deg); } }

/* Fail message box */
.ps-fail-msg {
  background: #fef2f2;
  border: 1px solid #fcd5d5;
  border-radius: 11px;
  padding: 14px 16px;
  font-size: 0.86rem; color: #d94f3d;
  margin-bottom: 20px; line-height: 1.5;
  word-break: break-word;
}

@media (max-width: 480px) {
  .ps-card { padding: 36px 22px; border-radius: 18px; }
  .ps-title { font-size: 1.5rem; }
  .ps-success-circle, .ps-fail-circle { width: 76px; height: 76px; }
  .ps-success-icon, .ps-fail-icon { font-size: 2.2rem; }
  .ps-buttons { flex-direction: column; }
  .ps-btn-primary, .ps-btn-outline { width: 100%; }
}
`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const { fetchCartItems, clearCart } = useCart();
  const [orderSaving,  setOrderSaving]  = useState(true);
  const [savedOrderId, setSavedOrderId] = useState(null);

  // Extract Inicis return params
  const resultCode = searchParams.get("resultCode") || "";
  const resultMsg  = searchParams.get("resultMsg")  || "";
  const tid        = searchParams.get("tid")        || "";
  const moid       = searchParams.get("MOID")       || "";
  const totPrice   = searchParams.get("TotPrice")   || "";
  const goodName   = searchParams.get("goodName")   || "";
  const applDate   = searchParams.get("applDate")   || "";
  const applTime   = searchParams.get("applTime")   || "";

  const isSuccess = resultCode === "0000";

  // ─── Format date "20260508" → "2026-05-08" ─────────────────────────────────
  const formatDate = (d, t) => {
    if (!d) return "";
    const date = `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6,8)}`;
    if (!t) return date;
    const time = `${t.slice(0,2)}:${t.slice(2,4)}:${t.slice(4,6)}`;
    return `${date} ${time}`;
  };

  // ─── Save order to backend after successful payment ───────────────────────
  useEffect(() => {
    const finalizeOrder = async () => {
      if (!isSuccess) {
        setOrderSaving(false);
        return;
      }

      // Get pending order data saved before payment
      const pendingOrderRaw = sessionStorage.getItem("pendingOrder");
      if (!pendingOrderRaw) {
        // No pending data — order may already be saved by webhook
        setOrderSaving(false);
        return;
      }

      try {
        const pendingOrder = JSON.parse(pendingOrderRaw);
        const isLoggedInUser = pendingOrder.isLoggedInUser;
        let newOrderId = null; // declared outside both branches so trackPurchase can read it below

        if (isLoggedInUser) {
          // Logged-in user order
          const res = await api.post("/orders", {
            shipping_address_id:    pendingOrder.addressId,
            billing_address_id:     pendingOrder.addressId,
            payment_method:         "inicis_card",
            note:                   pendingOrder.note || "",
            promo_code:             pendingOrder.promoCode || null,
            points_to_redeem:       pendingOrder.pointsToRedeem || 0,
            auto_create_transaction: true,
            confirmation_method:    "card",
            tid, moid,
          });
          newOrderId = res.data?.order_id || res.data?.order?._id;
          setSavedOrderId(newOrderId);
        } else {
          // Guest order
          const guestToken = localStorage.getItem("authToken");
          const res = await api.post("/orders/guest-checkout", {
            guest_token: guestToken,
            name:        `${pendingOrder.firstName} ${pendingOrder.lastName}`,
            email:       pendingOrder.email,
            phone:       pendingOrder.phone,
            // The real address the customer picked before paying — stashed whole
            // in sessionStorage by the checkout page.
            address: {
              ...(pendingOrder.address || {}),
              address_type: "shipping",
              full_name:    `${pendingOrder.firstName} ${pendingOrder.lastName}`,
              phone:        pendingOrder.phone,
            },
            payment_method:         "inicis_card",
            shipping_price:         pendingOrder.shipping || 0,
            discount_price:         pendingOrder.discount || 0,
            note:                   pendingOrder.notes    || "",
            promo_code:             pendingOrder.promoCode || null,
            auto_create_transaction: true,
            subtotal:               pendingOrder.subtotal,
            total:                  pendingOrder.total,
            confirmation_method:    "card",
            tid, moid,
          });
          newOrderId = res.data?.order_id || res.data?.order?._id;
          setSavedOrderId(newOrderId);
        }

        trackPurchase({
          orderId: newOrderId || moid,
          total: totPrice || pendingOrder.total,
        });

        sessionStorage.removeItem("pendingOrder");
        await clearCart();
        await fetchCartItems();
      } catch (err) {
        console.error("Failed to save order:", err);
        // Order was paid but not saved — admin will see in INIpay dashboard
      } finally {
        setOrderSaving(false);
      }
    };

    finalizeOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Success view ─────────────────────────────────────────────────────────
  if (isSuccess) {
    return (
      <>
        <style>{S}</style>
        <div className="ps-page">
          <div className="ps-card">

            {orderSaving ? (
              <div className="ps-loading">
                <div className="ps-spinner" />
                <p style={{ color: "#8a8a8a" }}>Confirming your order...</p>
              </div>
            ) : (
              <>
                <div className="ps-success-circle">
                  <span className="ps-success-icon">✓</span>
                </div>

                <h1 className="ps-title">Payment Successful!</h1>
                <p className="ps-subtitle">
                  Thank you for your order. We've sent a confirmation email with your receipt.
                </p>

                <div className="ps-details">
                  {savedOrderId && (
                    <div className="ps-detail-row">
                      <span className="ps-detail-label">Order ID</span>
                      <span className="ps-detail-value">{savedOrderId}</span>
                    </div>
                  )}
                  {totPrice && (
                    <div className="ps-detail-row">
                      <span className="ps-detail-label">Amount Paid</span>
                      <span className="ps-detail-value amount">
                        ₩{Number(totPrice).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {goodName && (
                    <div className="ps-detail-row">
                      <span className="ps-detail-label">Product</span>
                      <span className="ps-detail-value">{decodeURIComponent(goodName)}</span>
                    </div>
                  )}
                  {tid && (
                    <div className="ps-detail-row">
                      <span className="ps-detail-label">Transaction ID</span>
                      <span className="ps-detail-value" style={{ fontSize: "0.78rem" }}>{tid}</span>
                    </div>
                  )}
                  {applDate && (
                    <div className="ps-detail-row">
                      <span className="ps-detail-label">Date</span>
                      <span className="ps-detail-value">{formatDate(applDate, applTime)}</span>
                    </div>
                  )}
                </div>

                <div className="ps-buttons">
                  <Link to="/my-orders" className="ps-btn-primary">View My Orders</Link>
                  <Link to="/shop" className="ps-btn-outline">Continue Shopping</Link>
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  // ─── Failure view ─────────────────────────────────────────────────────────
  return (
    <>
      <style>{S}</style>
      <div className="ps-page">
        <div className="ps-card">
          <div className="ps-fail-circle">
            <span className="ps-fail-icon">✕</span>
          </div>

          <h1 className="ps-title">Payment Failed</h1>
          <p className="ps-subtitle">
            We couldn't process your payment. Don't worry — no charges were made.
          </p>

          {resultMsg && (
            <div className="ps-fail-msg">
              {decodeURIComponent(resultMsg)}
            </div>
          )}

          <div className="ps-buttons">
            <Link to="/cart" className="ps-btn-primary">Return to Cart</Link>
            <Link to="/" className="ps-btn-outline">Back to Home</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentSuccess;